import React, { useState, useCallback, useEffect, useMemo } from "react";
import { BaseTable } from "@app/components/common/BaseTable/BaseTable";
import { BaseButton } from "@app/components/common/BaseButton/BaseButton";
import { BaseForm } from "@app/components/common/forms/BaseForm/BaseForm";
import { BaseSpace } from "@app/components/common/BaseSpace/BaseSpace";
import { BasePopconfirm } from "@app/components/common/BasePopconfirm/BasePopconfirm";
import { PageTitle } from "@app/components/common/PageTitle/PageTitle";
import {
  message,
  Modal,
  Input,
  Select,
  Switch,
  Form,
  Row,
  Col,
  Avatar,
  Tag,
  Tooltip,
  InputNumber,
  Alert,
} from "antd";
import {
  SearchOutlined,
  SyncOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  adminApi,
  Contact,
  ContactPageParams,
  Employee,
  CreateContactData,
  UpdateContactData,
} from "@app/api/admin";
import { useUpdateEffect } from "@app/hooks/useUpdateEffect";
import { ColumnsType, Key } from "antd/lib/table/interface";
import dayjs from "dayjs";
import { useCompanyList } from "./CompanyPage";

export interface initialPaginationProps {
  current_page: number;
  per_page: number;
  total?: number;
  total_pages?: number;
}

export const initialPagination: initialPaginationProps = {
  current_page: 1,
  per_page: 20,
  total: 0,
  total_pages: 0,
};

interface ContactDepartment {
  id: number;
  name: string;
  employee_count: number;
}

const ContactPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [tableData, setTableData] = useState<{
    data: Contact[];
    pagination: initialPaginationProps;
  }>({
    data: [],
    pagination: initialPagination,
  });
  const [departments, setDepartments] = useState<ContactDepartment[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [searchForm] = Form.useForm();
  const [createForm] = Form.useForm();

  // 搜索参数
  const [searchParams, setSearchParams] = useState<ContactPageParams>({
    page: 1,
    page_size: 20,
  });

  // 获取通讯录列表
  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminApi.contacts.list(searchParams);
      setTableData({
        data: response.data.contacts,
        pagination: response.data.pagination,
      });
    } catch (error) {
      console.error("获取通讯录列表失败:", error);
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  // 获取部门列表
  const fetchDepartments = useCallback(async () => {
    try {
      const response = await adminApi.contacts.getDepartments();
      setDepartments(response.data);
    } catch (error) {
      console.error("获取部门列表失败:", error);
    }
  }, []);

  // 获取员工列表
  const fetchEmployees = useCallback(async () => {
    try {
      const response = await adminApi.employees.list();
      console.log("response.data.employees", response.data.employees);
      setEmployees(response.data.employees);
    } catch (error) {
      console.error("获取员工列表失败:", error);
    }
  }, []);

  useUpdateEffect(() => {
    fetchContacts();
    fetchDepartments();
    fetchEmployees();
  }, []);

  useUpdateEffect(() => {
    fetchContacts();
  }, [searchParams]);

  // 表格列定义
  const columns = useMemo(
    () => [
      {
        title: "头像",
        dataIndex: "avatar",
        key: "avatar",
        width: 80,
        render: (avatar: string, record: Contact) => (
          <Avatar size={40} src={avatar} icon={<UserOutlined />} />
        ),
      },
      {
        title: "姓名",
        dataIndex: "name",
        key: "name",
        width: 120,
        render: (name: string) => name,
      },
      {
        title: "电话",
        dataIndex: "phone",
        key: "phone",
        width: 120,
        render: (phone: string) => (
          <span>
            <PhoneOutlined style={{ marginRight: 4 }} />
            {phone}
          </span>
        ),
      },
      {
        title: "邮箱",
        dataIndex: "email",
        key: "email",
        width: 180,
        render: (email: string) =>
          email ? (
            <span>
              <MailOutlined style={{ marginRight: 4 }} />
              {email}
            </span>
          ) : (
            "-"
          ),
      },
      {
        title: "公司",
        dataIndex: "company",
        key: "company",
        width: 140,
        render: (company:any) => company?.name || "-",
      },
     
      {
        title: "部门",
        dataIndex: "department",
        key: "department",
        width: 120,
        render: (department: string) => department || "-",
      },
       {
        title: "职位",
        dataIndex: "position",
        key: "position",
        width: 120,
        render: (position: string) => position || "-",
      },
      {
        title: "入职日期",
        dataIndex: "hire_date",
        key: "hire_date",
        width: 120,
        render: (date: string) =>
          date ? dayjs(date).format("YYYY-MM-DD") : "-",
      },
      {
        title: "状态",
        dataIndex: "status",
        key: "status",
        width: 80,
        render: (status: string) => (
          <Tag
            color={
              status === "在职" ? "green" : status === "离职" ? "red" : "orange"
            }
          >
            {status}
          </Tag>
        ),
      },
      {
        title: "操作",
        key: "action",
        width: 220,
        fixed: "right" as const,
        render: (text: any, record: Contact) => (
          <BaseSpace>
            <BaseButton
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            >
              编辑
            </BaseButton>

            <BaseButton
              type="default"
              size="small"
              icon={<SyncOutlined />}
              onClick={() => handleSync(record)}
            >
              同步
            </BaseButton>
            <BasePopconfirm
              title="确定要删除这个联系人吗？"
              onConfirm={() => handleDelete(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <BaseButton
                type="primary"
                danger
                size="small"
                icon={<DeleteOutlined />}
              >
                删除
              </BaseButton>
            </BasePopconfirm>
          </BaseSpace>
        ),
      },
    ],
    [employees]
  );

  // 搜索
  const handleSearch = (values: any) => {
    setSearchParams({
      ...searchParams,
      ...values,
      page: 1,
      // search: values.search,
      // department_id: values.department_id,
      // status: values.status,
    });
  };

  // 重置搜索
  const handleReset = () => {
    searchForm.resetFields();
    setSearchParams({
      page: 1,
      page_size: 20,
    });
  };

  // 表格分页变化
  const handleTableChange = (page: number, pageSize?: number) => {
    setSearchParams({
      ...searchParams,
      page,
      page_size: pageSize || 20,
    });
  };

  // 编辑
  const handleEdit = (record: Contact) => {
    setIsEdit(true);
    setIsCreateModalVisible(true);
    // 由于新接口中Contact不包含employee_id等字段，这里只能设置基本信息
    createForm.setFieldsValue({
      id: record.id,
      employee_id: undefined, // 需要从员工列表中匹配
      is_public: true,
      display_order: 0,
      custom_title: record.position,
    });
  };

  // 删除
  const handleDelete = async (id: number) => {
    try {
      await adminApi.contacts.delete(id);
      message.success("删除成功");
      fetchContacts();
    } catch (error) {
      console.error("删除失败:", error);
    }
  };

  // 同步单个联系人
  const handleSync = async (record: Contact) => {
    // 由于新接口中Contact不包含employee_id，需要通过姓名匹配员工
    const employee = employees.find((emp) => emp.name === record.name);
    console.log("employee", employees);

    if (!employee) {
      message.warning("未找到对应的员工信息，无法同步");
      return;
    }

    try {
      await adminApi.contacts.syncEmployee({ employee_id: employee.id });
      message.success("同步成功");
      fetchContacts();
    } catch (error) {
      console.error("同步失败:", error);
    }
  };

  // 批量同步
  const handleBatchSync = async () => {
    setSyncLoading(true);
    try {
      await adminApi.contacts.batchSyncEmployees({
        employee_ids: selectedRowKeys.map(Number),
      });
      message.success("批量同步成功");
      fetchContacts();
      setSelectedRowKeys([]);
    } catch (error) {
      console.error("批量同步失败:", error);
    } finally {
      setSyncLoading(false);
    }
  };

  // 新增联系人
  const handleCreate = () => {
    setIsEdit(false);
    setIsCreateModalVisible(true);
    createForm.resetFields();
  };

  // 提交表单
  const handleCreateSubmit = async () => {
    try {
      const values = await createForm.validateFields();
      setCreateLoading(true);

      if (isEdit) {
        const updateData: UpdateContactData = {
          employee_id: values.employee_id,
          is_public: values.is_public,
          display_order: values.display_order,
          custom_title: values.custom_title,
        };
        await adminApi.contacts.update(values.id, updateData);
      } else {
        const createData: CreateContactData = {
          employee_id: values.employee_id,
          is_public: values.is_public,
          display_order: values.display_order,
          custom_title: values.custom_title,
        };
        await adminApi.contacts.create(createData);
      }

      fetchContacts();
      message.success(isEdit ? "编辑成功" : "创建成功");
      setIsCreateModalVisible(false);
      createForm.resetFields();
    } catch (error: any) {
      console.log(error);
    } finally {
      setCreateLoading(false);
    }
  };

  // 关闭弹窗
  const handleCreateCancel = () => {
    setIsCreateModalVisible(false);
    createForm.resetFields();
  };

  // 行选择配置
  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys: Key[]) => {
      setSelectedRowKeys(selectedRowKeys);
    },
  };
  const { companyList } = useCompanyList();

  return (
    <>
      <PageTitle>通讯录管理</PageTitle>
      <Alert
        type="info"
        message="页面功能说明"
        description="通讯录用于设置小程序端通讯录页面显示的员工，可通过新增、编辑与同步功能维护展示信息"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {/* 搜索过滤 */}
      <Form
        form={searchForm}
        layout="inline"
        onFinish={handleSearch}
        style={{ marginBottom: 16 }}
      >
        <Row gutter={16} style={{ width: "100%" }}>
          <Col span={18}>
            <BaseSpace wrap>
              <Form.Item name="search">
                <Input
                  placeholder="搜索姓名、部门、职位"
                  prefix={<SearchOutlined />}
                  style={{ width: 300 }}
                />
              </Form.Item>
              {/* <Form.Item name="department_id">
                <Select
                  placeholder="选择部门"
                  allowClear
                  style={{ width: 150 }}
                >
                  {departments.map((dept) => (
                    <Select.Option key={dept.id} value={dept.id}>
                      {dept.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item> */}
              {/* <Form.Item name="company_id">
                <Select placeholder="请选择公司" style={{ width: 200 }}>
                  {companyList.map((item) => (
                    <Select.Option
                      key={item.id}
                      value={item.id}
                      disabled={isEdit}
                    >
                      {item.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item> */}

              <Form.Item name="status">
                <Select
                  placeholder="员工状态"
                  allowClear
                  style={{ width: 120 }}
                >
                  <Select.Option value="在职">在职</Select.Option>
                  <Select.Option value="离职">离职</Select.Option>
                  <Select.Option value="试用期">试用期</Select.Option>
                </Select>
              </Form.Item>
              <BaseButton type="primary" htmlType="submit">
                搜索
              </BaseButton>
              <BaseButton onClick={handleReset}>重置</BaseButton>
            </BaseSpace>
          </Col>
          <Col span={6} style={{ textAlign: "right" }}>
            <BaseSpace>
              <BaseButton
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreate}
              >
                新增联系人
              </BaseButton>
              <BaseButton
                type="default"
                icon={<SyncOutlined />}
                onClick={handleBatchSync}
                loading={syncLoading}
              >
                批量同步
              </BaseButton>
            </BaseSpace>
          </Col>
        </Row>
      </Form>

      <BaseTable
        bordered
        dataSource={tableData.data}
        columns={columns}
        rowSelection={rowSelection}
        scroll={{ x: 1600 }}
        pagination={{
          current: tableData.pagination.current_page,
          pageSize: tableData.pagination.per_page,
          total: tableData.pagination.total,
          onChange: handleTableChange,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
        }}
        loading={loading}
        rowKey="id"
      />

      {/* 创建/编辑模态框 */}
      <Modal
        title={isEdit ? "编辑联系人" : "创建联系人"}
        visible={isCreateModalVisible}
        onOk={handleCreateSubmit}
        onCancel={handleCreateCancel}
        confirmLoading={createLoading}
        width={600}
        destroyOnClose
      >
        <BaseForm
          form={createForm}
          layout="vertical"
          style={{ maxHeight: "60vh", overflowY: "auto" }}
        >
          <BaseForm.Item label="ID" name="id" hidden>
            <Input />
          </BaseForm.Item>

          <BaseForm.Item
            label="关联员工"
            name="employee_id"
            rules={[{ required: true, message: "请选择关联员工" }]}
          >
            <Select
              placeholder="请选择关联员工"
              allowClear
              showSearch
              //   filterOption={(input, option) =>
              //     (option?.children as string)?.toLowerCase().includes(input.toLowerCase())
              //   }
            >
              {employees.map((emp) => (
                <Select.Option key={emp.id} value={emp.id}>
                  {emp.name} - {emp.phone_number}
                </Select.Option>
              ))}
            </Select>
          </BaseForm.Item>

          <BaseForm.Item
            label="是否公开"
            name="is_public"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch checkedChildren="公开" unCheckedChildren="私有" />
          </BaseForm.Item>

          <BaseForm.Item label="显示顺序" name="display_order">
            <InputNumber
              placeholder="请输入显示顺序"
              min={0}
              style={{ width: "100%" }}
            />
          </BaseForm.Item>

          <BaseForm.Item label="自定义名称" name="custom_title">
            <Input placeholder="请输入自定义名称" />
          </BaseForm.Item>
        </BaseForm>
      </Modal>
    </>
  );
};

export default ContactPage;

export const useContactList = () => {
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState<Contact[]>([]);

  const fetchContacts = useCallback(async (params?: ContactPageParams) => {
    setLoading(true);
    try {
      const response = await adminApi.contacts.list(params);
      setContacts(response.data.contacts);
      return response.data;
    } catch (error) {
      console.error("获取通讯录列表失败:", error);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    contacts,
    fetchContacts,
  };
};
